/**
 * The queue of changes the server has not seen yet.
 *
 * Every mutation goes through here, online or not. That is what makes the app work the same
 * in a basement freezer as it does on wifi: there is no "try the network first" path that
 * behaves differently when the network is not there.
 */

import type { QueuedMutation, QueuedOperation } from '../api/types';
import type { Database, OutboxEntry } from './database';

/**
 * Queues a mutation and returns the key it will be sent under.
 *
 * The key is minted here, once, and stored with the mutation. Every later attempt reuses it,
 * which is precisely what makes a retry harmless — a key minted at send time would make each
 * attempt look like a new request to the server.
 */
export async function enqueue(db: Database, operation: QueuedOperation): Promise<string> {
  const key = newKey();

  await db.add('outbox', {
    key,
    operation,
    queuedAt: new Date().toISOString(),
  });

  return key;
}

/** Everything waiting to be sent, oldest first. */
export function pending(db: Database): Promise<OutboxEntry[]> {
  return db.getAll('outbox');
}

export async function pendingCount(db: Database): Promise<number> {
  return db.count('outbox');
}

/** Drops entries the server has accounted for, whether it applied them or refused them. */
export async function forget(db: Database, keys: Iterable<string>): Promise<void> {
  const dropping = new Set(keys);
  const transaction = db.transaction('outbox', 'readwrite');

  for (const entry of await transaction.store.getAll()) {
    if (dropping.has(entry.key) && entry.position !== undefined) {
      await transaction.store.delete(entry.position);
    }
  }

  await transaction.done;
}

export function asMutations(entries: OutboxEntry[]): QueuedMutation[] {
  return entries.map(({ key, operation }) => ({ key, operation }));
}

function newKey(): string {
  return crypto.randomUUID();
}
